import type { NodeDefinition } from '@cognite/sdk';
import dayjs from 'dayjs';

import { instancesToColumns, viewToSchema } from '#core/lib/cognite/view.ts';
import type { AstInput } from '#core/lib/expr/normalize.ts';
import {
  type ColumnDoc,
  type ColumnType,
  numberCol,
  projectSchema,
  type Schema,
  schemaFromEntries,
  stringCol,
  timestampCol,
  unwrapNullable,
  type ViewDescription,
} from '#core/lib/expr/schema/types.ts';
import { type Cell, MaterializedDataset } from '#core/lib/expr/value.ts';

import { type AncestorScopedAssemble, describeAncestorColumns, makeAncestorScopedProvider } from './ancestorScope.ts';
import { numericColForSlug, unitForKind } from './columnUnits.ts';
import type { VirtualView } from './createReshapedView.ts';
import { type AncestorRelation, ancestorStruct } from './enrichAncestors.ts';
import {
  fetchSeriesByHandle,
  handleKey,
  type MeasureRefs,
  normalizeValueToBase,
  rollingWindowMs,
} from './fetchDatapointsLong.ts';
import { resolveColumnSet } from './resolveColumns.ts';
import {
  assembleContextAndAncestorColumns,
  broadcastByOwner,
  buildMeasureRefs,
  type ExplosionViewSpecBase,
  explosionSourceColumns,
  resolveAncestorsByRelation,
  resolveExplosionColumns,
} from './seriesExplosion.ts';
import type { EnrichGroup, ResolvedFlat } from './shapes/index.ts';
import { seriesToColumns, TS_REF_DEFAULT_WINDOW_DAYS } from '#plugin-chat/lib/source-fetchers/helpers.ts';

/** The aligned run/datapoint index column every aligned-series row carries. */
const TIMESTAMP_COLUMN = 'timestamp';
const timestampDoc: ColumnDoc = {
  name: TIMESTAMP_COLUMN,
  type: timestampCol,
  description: 'The aligned run/datapoint timestamp (one row per source row × run timestamp).',
};

/** Declarative spec for a timestamp-aligned, wide-typed series view. */
export type AlignedSeriesViewSpec = ExplosionViewSpecBase & {
  /** A fixed predicate body (source terms) AND-ed into the source read at plan time. */
  readonly where?: AstInput;
  /** The timeseries-handle column group aligned into one typed scalar column per measure. */
  readonly groups: EnrichGroup;
};

/**
 * One source row's per-measure series at the timestamp it owns, projected to
 * the timestamp → value lookups the outer-join reads.
 */
type RowSeries = {
  /** The source-row index this aligned row was expanded from. */
  readonly owner: number;
  /** The aligned timestamp this row carries. */
  readonly timestamp: Cell;
  /** Canonical measure name → the measure's value at `timestamp` (null when absent). */
  readonly valuesByMeasure: ReadonlyMap<string, Cell>;
};

/**
 * Build a timestamp-aligned, wide-typed view: one row per (source row × run
 * timestamp), each measure a TYPED scalar value column carrying its unit, plus a
 * `timestamp` column and the broadcast kept context + ancestor columns. The
 * MODE-B replacement for the long flattening: instead of one untyped `value`
 * column tagged by a `measure` label, each measure is its own typed column and
 * missing datapoints are null.
 */
export const createAlignedSeriesView = (spec: AlignedSeriesViewSpec): VirtualView => {
  const { source, keep, groups, where, enrich, client, mintSeries } = spec;
  if (!mintSeries) {
    throw new Error('mintSeries required to mint per-row view timeseries cells');
  }
  const windowDays = spec.defaultWindowDays ?? TS_REF_DEFAULT_WINDOW_DAYS;

  // SSOT for de-prefix/rename/typing/docs/units of the kept context columns.
  const { declared: keepSchema, columns: keepDocs } = resolveColumnSet(source, keep);
  // The flat source projection (kept context + measure handles) shared with the
  // long flattening; the two factories differ only in the assembly step.
  const { keepCols, measureCols } = resolveExplosionColumns(source, keep, groups);
  const relations: readonly AncestorRelation[] = enrich?.relations ?? [];

  // The source schema, whose measure handles are `nested-dataset` timeseries
  // refs carrying their datapoint VALUE type (string-valued measures like
  // `comment`/`executedBy` vs numeric rate measures).
  const sourceSchema: Schema = viewToSchema(source);

  // The inner datapoint value type a measure's source timeseries ref carries,
  // read off the source schema's `nested-dataset` inner `value` column. Defaults
  // to numeric for any non-TS-ref handle (no inner schema to unwrap).
  const measureValueType = (sourceColumn: string): ColumnType => {
    const handle = sourceSchema.get(sourceColumn);
    const unwrapped = handle === undefined ? undefined : unwrapNullable(handle);
    const inner = unwrapped?.kind === 'nested-dataset' ? unwrapped.innerSchema.get('value') : undefined;
    return inner === undefined ? numberCol : unwrapNullable(inner);
  };

  // Each measure column carries the datapoint VALUE, not the source timeseries
  // handle: a typed scalar resolved by the typing ladder (most authoritative
  // first). An aligned-series view fronts NUMERIC output measures, so the value
  // type defaults to numeric — only an explicit annotation or a string
  // discriminator diverts it:
  //
  //   - explicit shape `valueType` (step 1) wins outright: `'date'` ⇒ unit-less
  //     `timestampCol` (epoch-ms instants render as dates), `'string'` ⇒
  //     unit-less `stringCol`, `'number'` ⇒ numeric carrying the canonical
  //     name's unit;
  //   - a source ref carrying a CDM `CogniteTimeSeriesType` string discriminator
  //     (step 2) ⇒ `stringCol`;
  //   - otherwise the measure is numeric, carrying the canonical name's unit
  //     where one resolves. A discriminator-less, unit-less measure is still
  //     numeric (an aligned output series is numeric by construction); a
  //     genuinely string- or date-valued aligned measure carries an explicit
  //     `valueType`.
  const measureType = (c: ResolvedFlat): ColumnType => {
    if (c.valueType === 'date') {
      return timestampCol;
    }
    if (c.valueType === 'string') {
      return stringCol;
    }
    if (c.valueType === undefined && measureValueType(c.source).kind === 'string') {
      return stringCol;
    }
    return numericColForSlug(unitForKind(c.kind));
  };

  // Engine schema: typed kept context + typed measure VALUE columns (units
  // intact) + the aligned timestamp + optional ancestor structs. NOT the four
  // long columns.
  const declared: Schema = schemaFromEntries([
    ...keepSchema.entries(),
    ...measureCols.map((c: ResolvedFlat) => [c.canonical, measureType(c)] as const),
    [TIMESTAMP_COLUMN, timestampCol] as const,
    ...relations.map((rel: AncestorRelation) => [rel, ancestorStruct] as const),
  ]);

  // Agent docs: typed kept context + typed measure values + timestamp + ancestors.
  const measureDocs: readonly ColumnDoc[] = measureCols.map(
    (c: ResolvedFlat): ColumnDoc => ({ name: c.canonical, type: measureType(c), description: c.description }),
  );
  const ancestorDocs: ColumnDoc[] = describeAncestorColumns(relations);
  const viewDoc: ViewDescription = {
    description: spec.description,
    columns: [...keepDocs, ...measureDocs, timestampDoc, ...ancestorDocs],
  };

  // The source projection: kept context columns + the measure handle columns.
  const sourceColumns: readonly string[] = explosionSourceColumns({ keepCols, measureCols });

  // The scaffold + fetch HEAD (ancestor-scope resolution + scoped source read)
  // are shared with the enrich/flatten factories; the timestamp-aligned wide
  // assembly below is this builder's projection step.
  return makeAncestorScopedProvider({
    name: spec.name,
    declared,
    viewDoc,
    source,
    cols: keepCols,
    sourceColumns,
    relations,
    enrich,
    client,
    mintSeries,
    where,
    assemble: async ({ rows, ctx }: Parameters<AncestorScopedAssemble>[0]): Promise<MaterializedDataset> => {
      // Context columns, de-prefixed/renamed source → canonical.
      const contextSchema = projectSchema(
        viewToSchema(source),
        keepCols.map((c: ResolvedFlat) => c.source),
      );
      const projected = instancesToColumns(rows, contextSchema, source, mintSeries);
      const context: Record<string, readonly Cell[]> = Object.fromEntries(
        keepCols.map((c: ResolvedFlat) => [c.canonical, projected[c.source] ?? []] as const),
      );

      // Per-measure, per-source-row timeseries handles, read RAW off the node
      // rows; the I/O (distinct fetch + session memoization) is shared with
      // the long flattening.
      const measureRefs: readonly MeasureRefs[] = buildMeasureRefs(rows, source, measureCols);
      const { start, end } = rollingWindowMs(windowDays);
      const seriesByKey = await fetchSeriesByHandle(client, ctx, measureRefs, { start, end });

      // Per source row, per measure: the ordered (timestamp, value) datapoints.
      // The series for a row's handle is the projection of the fetched
      // datapoints; a row with no handle for a measure contributes none.
      type Datapoint = { readonly ts: Cell; readonly value: Cell };
      const rowMeasureLookups: readonly ReadonlyMap<string, readonly Datapoint[]>[] = rows.map(
        (_row: NodeDefinition, rowIndex: number): ReadonlyMap<string, readonly Datapoint[]> =>
          new Map(
            measureRefs.map((m: MeasureRefs): readonly [string, readonly Datapoint[]] => {
              const ref = m.refs[rowIndex];
              const series = ref === null || ref === undefined ? undefined : seriesByKey.get(handleKey(ref));
              if (series === undefined) {
                return [m.canonical, []];
              }
              const cols = seriesToColumns(series);
              const timestamps = cols.timestamp ?? [];
              return [
                m.canonical,
                timestamps.map(
                  (ts: Cell, j: number): Datapoint => ({
                    ts,
                    value: normalizeValueToBase(cols.value?.[j] ?? null, m.storedUnit),
                  }),
                ),
              ];
            }),
          ),
      );

      // Minute-granularity date key for a raw epoch-ms timestamp. A run's
      // oil/gas/water datapoints carry sub-minute-jittered timestamps, so
      // unioning RAW timestamps would split one run across three rows; bucket
      // to the minute so all phases of a run land on ONE aligned row.
      const minuteKey = (ts: Cell): string => dayjs(Number(ts)).format('YYYY-MM-DD HH:mm');

      // TIMESTAMP OUTER-JOIN (single pass): per source row, the union of
      // minute-buckets across that row's measures; one aligned row per (source
      // row × minute) with each measure's first non-null value within that
      // bucket (null absent). The row's timestamp is the minimum raw epoch in
      // the bucket; rows are sorted by that representative timestamp. Each
      // datapoint's minute key is computed once and the bucketing groups in a
      // single pass over the tagged datapoints.
      type Tagged = { readonly key: string; readonly canonical: string; readonly ts: Cell; readonly value: Cell };
      const aligned: readonly RowSeries[] = rows.flatMap(
        (_row: NodeDefinition, rowIndex: number): readonly RowSeries[] => {
          const lookups = rowMeasureLookups[rowIndex];
          const tagged: readonly Tagged[] = measureCols.flatMap((c: ResolvedFlat): readonly Tagged[] =>
            (lookups?.get(c.canonical) ?? []).map(
              (d: Datapoint): Tagged => ({ key: minuteKey(d.ts), canonical: c.canonical, ts: d.ts, value: d.value }),
            ),
          );
          return Array.from(
            Map.groupBy(tagged, (e: Tagged) => e.key).values(),
            (entries: readonly Tagged[]): RowSeries => {
              // `Map.groupBy` never yields empty groups, so the first datapoint
              // always exists — guard makes that invariant legible to the checker.
              const first = entries[0];
              if (first === undefined) {
                throw new Error('Map.groupBy yielded an empty bucket');
              }
              return {
                owner: rowIndex,
                timestamp: entries.reduce(
                  (min: Cell, e: Tagged) => (Number(e.ts) < Number(min) ? e.ts : min),
                  first.ts,
                ),
                valuesByMeasure: new Map(
                  Array.from(
                    Map.groupBy(entries, (e: Tagged) => e.canonical),
                    ([canonical, es]: readonly [string, readonly Tagged[]]) => {
                      const firstNonNull = es.find((e: Tagged) => e.value !== null);
                      return [canonical, firstNonNull?.value ?? null] as const;
                    },
                  ).filter(([, value]: readonly [string, Cell]) => value !== null),
                ),
              };
            },
          ).toSorted((a: RowSeries, b: RowSeries) => Number(a.timestamp) - Number(b.timestamp));
        },
      );

      // Optional ancestry, resolved once over the source rows, then broadcast
      // per owning source-row index alongside the kept context.
      const ancestorsByRel = await resolveAncestorsByRelation({
        client,
        ctx,
        context,
        enrich,
        relations,
        mintSeries,
      });

      // Broadcast a source-grain column onto each aligned row by owner index.
      const broadcast = broadcastByOwner(aligned.map((r: RowSeries) => r.owner));

      const measureColumns: Record<string, readonly Cell[]> = Object.fromEntries(
        measureCols.map(
          (c: ResolvedFlat) =>
            [c.canonical, aligned.map((r: RowSeries) => r.valuesByMeasure.get(c.canonical) ?? null)] as const,
        ),
      );

      const columns: Record<string, readonly Cell[]> = {
        ...assembleContextAndAncestorColumns({ keepCols, relations, context, ancestorsByRel, broadcast }),
        ...measureColumns,
        [TIMESTAMP_COLUMN]: aligned.map((r: RowSeries) => r.timestamp),
      };

      // The measure value types are fully determined at declare time (the
      // typing ladder resolves every aligned measure to a concrete scalar
      // type), so the materialized schema IS the declared schema — no
      // fetch-time `unknown`→concrete resolution is needed.
      return new MaterializedDataset(columns, declared);
    },
  });
};
